import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { ButtonV2, H1, P, Spinner, reportError } from 'shared';
import { usePostConsumerV2ReceiptsReceiptUuidIdentityVerificationMutation, usePostConsumerV2ReceiptsReceiptUuidIdentityVerificationCompleteMutation, } from 'consumer-api-custom';
const PlaidIdentityVerification = ({ linkToken, onPlaidSuccess, onPlaidExit, }) => {
    const onPlaidSuccessRef = useRef(onPlaidSuccess);
    onPlaidSuccessRef.current = onPlaidSuccess;
    const onPlaidExitRef = useRef(onPlaidExit);
    onPlaidExitRef.current = onPlaidExit;
    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: () => onPlaidSuccessRef.current(),
        onExit: () => onPlaidExitRef.current(),
    });
    useEffect(() => {
        if (ready) {
            open();
        }
    }, [ready, open]);
    return null;
};
const IdentityVerification = ({ receiptUuid, onComplete, }) => {
    const [verificationStep, setVerificationStep] = useState('loading');
    const [linkToken, setLinkToken] = useState(null);
    const [verificationId, setVerificationId] = useState(null);
    const [createIdentityVerification, { isLoading: isCreatingVerification }] = usePostConsumerV2ReceiptsReceiptUuidIdentityVerificationMutation();
    const [completeIdentityVerification] = usePostConsumerV2ReceiptsReceiptUuidIdentityVerificationCompleteMutation();
    const verificationStepRef = useRef(verificationStep);
    verificationStepRef.current = verificationStep;
    const verificationIdRef = useRef(verificationId);
    verificationIdRef.current = verificationId;
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;
    const handlePlaidSuccess = () => {
        const currentVerificationId = verificationIdRef.current;
        if (!currentVerificationId) {
            reportError('flashError', {
                componentName: 'IdentityVerification',
                funcName: 'handlePlaidSuccess',
                errType: 'identity_verification',
                errURL: window.location.pathname,
                errMsg: 'Missing verificationId after Plaid success',
            });
            setVerificationStep('failed');
            return;
        }
        setVerificationStep('loading');
        completeIdentityVerification({
            receiptUuid,
            body: { identityVerificationId: currentVerificationId },
        }).then((result) => {
            if ('data' in result && result.data.status === 'success') {
                onCompleteRef.current();
            }
            else {
                setVerificationStep('failed');
            }
        });
    };
    const handlePlaidExit = () => {
        if (verificationStepRef.current !== 'loading') {
            setVerificationStep('failed');
        }
    };
    const handleRetryVerification = () => {
        setLinkToken(null);
        setVerificationId(null);
        createIdentityVerification({
            receiptUuid,
        }).then((result) => {
            if ('data' in result && result.data.identityVerificationRequired) {
                if (result.data.linkToken && result.data.verificationId) {
                    setLinkToken(result.data.linkToken);
                    setVerificationId(result.data.verificationId);
                    setVerificationStep('verifying');
                }
                else {
                    reportError('flashError', {
                        componentName: 'IdentityVerification',
                        funcName: 'handleRetryVerification',
                        errType: 'identity_verification',
                        errURL: window.location.pathname,
                        errMsg: 'Missing linkToken or verificationId in verification response',
                    });
                    setVerificationStep('failed');
                }
            }
            else if ('data' in result &&
                !result.data.identityVerificationRequired) {
                onCompleteRef.current();
            }
        });
    };
    useEffect(() => {
        createIdentityVerification({
            receiptUuid,
        }).then((result) => {
            if ('data' in result) {
                if (!result.data.identityVerificationRequired) {
                    onCompleteRef.current();
                }
                else if (result.data.linkToken && result.data.verificationId) {
                    setLinkToken(result.data.linkToken);
                    setVerificationId(result.data.verificationId);
                    setVerificationStep('verifying');
                }
                else {
                    reportError('flashError', {
                        componentName: 'IdentityVerification',
                        funcName: 'createIdentityVerification',
                        errType: 'identity_verification',
                        errURL: window.location.pathname,
                        errMsg: 'Missing linkToken or verificationId in verification response',
                    });
                    setVerificationStep('failed');
                }
            }
            else {
                reportError('flashError', {
                    componentName: 'IdentityVerification',
                    funcName: 'createIdentityVerification',
                    errType: 'identity_verification',
                    errURL: window.location.pathname,
                    errMsg: 'Unable to start identity verification',
                });
                setVerificationStep('failed');
            }
        });
    }, []);
    if (verificationStep === 'loading') {
        return (_jsx("div", { className: "esign-container", children: _jsx("div", { className: "identity-verification-container", children: _jsx(Spinner, {}) }) }));
    }
    if (verificationStep === 'verifying' && linkToken) {
        return (_jsx("div", { className: "esign-container", children: _jsxs("div", { className: "identity-verification-container", children: [_jsx(H1, { className: "first-title", children: "Identity Verification Required" }), _jsx(P, { variant: "title-large", className: "mar-t-24", children: "Before signing your receipt, we need to verify your identity." }), _jsx(PlaidIdentityVerification, { linkToken: linkToken, onPlaidSuccess: handlePlaidSuccess, onPlaidExit: handlePlaidExit }, linkToken)] }) }));
    }
    if (verificationStep === 'failed') {
        return (_jsx("div", { className: "esign-container", children: _jsxs("div", { className: "identity-verification-container", children: [_jsx(H1, { className: "first-title", children: "Identity Verification Required" }), _jsx(P, { variant: "title-large", className: "mar-t-24", children: "We were unable to verify your identity. Please try again." }), _jsx(ButtonV2, { type: "button", className: "mar-t-24", submitting: isCreatingVerification, onClick: handleRetryVerification, children: "Retry Verification" })] }) }));
    }
    return (_jsx("div", { className: "esign-container", children: _jsx("div", { className: "identity-verification-container", children: _jsx(Spinner, {}) }) }));
};
export default IdentityVerification;
